import React, { useState } from 'react';
import Panel from './Panel';
import { STAGING_AREA_ACTIONS } from './panelActions';
import StagingRecipientChip from './StagingRecipientChip';
import StagingSummaryBlock from './StagingSummaryBlock';
import LinkedSurveysList from './LinkedSurveysList';
import ReLinkConfirmModal from './ReLinkConfirmModal';
import SendConfirmModal from './SendConfirmModal';
import SendResultsModal from './SendResultsModal';
import useContextMenu from '../hooks/useContextMenu';
import useLayoutDrag from '../hooks/useLayoutDrag';
import ContextMenu from './ContextMenu';
import {
    effectiveCRMChannels,
    selectableChannels,
    selectableCRMChannels,
} from '../utils/channelInventory';
import {
    controlFor,
    productionChannelDisabledReason,
} from '../utils/channelControlApi';

export default function StagingArea({ staging, onOpenRecipients, onReSendSent, recipientDisplayNames = {}, style, channelControls = null }) {
    const [confirmClear, setConfirmClear] = useState(false);
    const [showReLinkModal, setShowReLinkModal] = useState(null);
    const [showSendModal, setShowSendModal] = useState(false);
    const [updatingCRMChannels, setUpdatingCRMChannels] = useState(false);
    const { openMenu, contextMenuProps } = useContextMenu();
    const { dropHandlers, activeDropTarget } = useLayoutDrag();

    const {
        stagedIds,
        remove,
        clear,
        count,
        stagingChannel,
        dispatchMode,
        crmOfferedChannels = [],
        dispatchPreview,
        dispatchCapabilities = [],
        setStagingChannel,
        setCRMChannels,
        prepareSendReview,
        linkedSurveys = [],
        linkSurvey,
        unlinkSurvey,
        sendAll,
        // Send-monitor surface — owned by the useStagingArea hook so the modal
        // can be shown/hidden independently of the send loop, and can be
        // re-opened from the CommandBar's Recipients dropdown.
        sendInProgress,
        sendResult,
        monitorOpen,
        openSendMonitor,
        closeSendMonitor,
        dismissSendResult,
        cancelSend,
        // WP3: server-state reconciliation notice (stale staged entries dropped).
        reconcileNotice,
        dismissReconcileNotice,
    } = staging;
    const dropActive = activeDropTarget === 'staging-area';
    const selectedMethod = dispatchMode === 'channel_response' ? 'channel_response' : (stagingChannel || 'browser');
    const productionDisabledReason = productionChannelDisabledReason(channelControls, dispatchMode === 'channel_response' ? 'email' : selectedMethod);

    const actions = STAGING_AREA_ACTIONS.map(a => {
        if (a.label === '+ Recipients') return { ...a, onClick: () => onOpenRecipients?.() };
        if (a.label === 'Send') return {
            ...a,
            onClick: async () => {
                if (productionDisabledReason) return;
                try {
                    const review = await prepareSendReview?.();
                    if (review) setShowSendModal(true);
                } catch (error) {
                    // The hook's caller owns the visible error surface.
                }
            },
            disabledWhen: () => Boolean(productionDisabledReason),
            disabledReason: () => productionDisabledReason,
        };
        if (a.label === 'Clear') return { ...a, onClick: () => { if (count > 0) setConfirmClear(true); } };
        return a;
    });

    const toLinkedSurvey = (payload) => ({
        surveyId: payload.surveyId,
        deploymentId: payload.deploymentId,
        deploymentSlug: payload.deploymentSlug,
        surveyName: payload.surveyName,
        // True server lifecycle state ('deployed' | 'sent'), carried from the
        // drag payload so the drop can detect an already-sent survey.
        lifecycleStatus: payload.lifecycleStatus,
        designedChannels: payload.designedChannels,
        surveyVersionId: payload.surveyVersionId,
    });

    const handleSurveyDrop = (payload) => {
        const linkedSurvey = toLinkedSurvey(payload);
        if (!linkedSurvey.deploymentId) {
            console.warn('[Staging] Cannot link dropped survey without deploymentId', payload);
            return;
        }

        // Dragging an already-SENT survey into the Staging Area transparently
        // re-deploys it for send: the current wave is closed (completion-checked)
        // and a fresh deployment is staged. The undeploy → deploy → stage
        // orchestration lives in App (onReSendSent) where the confirm modal and
        // refresh do. Non-'sent' surveys keep the normal stage path below.
        if (linkedSurvey.lifecycleStatus === 'sent') {
            onReSendSent?.(linkedSurvey);
            return;
        }

        // TODO v2: Per-recipient re-link breakdown. The current re-link flow fires
        // only when the same deployment is dropped again, so alreadyLinked = count
        // and newlyLinked = 0 is correct for this case. The spec's partial-overlap
        // breakdown ("44 newly linked, 3 already linked") applies to first-link
        // scenarios where some recipients already have links from another deployment.
        // Requires: GET /deployments/:id/recipient_links to compute real overlap.
        const alreadyLinked = linkedSurveys.some(s => s.deploymentId === linkedSurvey.deploymentId);
        if (alreadyLinked) {
            setShowReLinkModal(linkedSurvey);
            return;
        }

        linkSurvey?.(linkedSurvey);
    };

    const handleConfirmSend = () => {
        if (productionDisabledReason) return;
        // Kick off the per-recipient loop and immediately close the confirm
        // modal. SendResultsModal will auto-open because the hook flips
        // monitorOpen=true at the start of sendAll().
        sendAll?.(dispatchPreview).catch(() => {});
        setShowSendModal(false);
    };

    const handleChipContextMenu = (e, recipientId) => {
        openMenu(e, [
            { label: 'Remove from Staging', onClick: () => remove(recipientId) },
        ]);
    };

    const getRecipientDisplayName = (id) => {
        const recipientInfo = recipientDisplayNames[id];
        if (recipientInfo && typeof recipientInfo === 'object') {
            return recipientInfo.displayName || recipientInfo.name || recipientInfo.email || id;
        }
        return recipientInfo || id;
    };

    const recipients = [...stagedIds].map(id => {
        const recipientInfo = recipientDisplayNames[id];
        const hasMetadata = recipientInfo && typeof recipientInfo === 'object';
        return {
            id,
            displayName: getRecipientDisplayName(id),
            name: hasMetadata ? (recipientInfo.name || '') : '',
            email: hasMetadata ? (recipientInfo.email || '') : '',
            channel: hasMetadata ? (recipientInfo.channel || 'email') : 'email',
        };
    });

    const designedChannels = (() => {
        const survey = linkedSurveys[0] || {};
        const raw = survey.designedChannels || survey.designed_channels || survey.targetChannels || survey.target_channels;
        if (Array.isArray(raw) && raw.length) return raw.map((channel) => channel === 'web' || channel === 'email' ? 'browser' : channel);
        return selectableChannels().map((channel) => channel.token);
    })();
    const capabilityByChannel = new Map(dispatchCapabilities.map((item) => [item.channel, item]));
    const directChannels = designedChannels.filter((channel) => capabilityByChannel.get(channel)?.supports_dcm);
    const crmChannels = selectableCRMChannels(designedChannels, dispatchCapabilities);
    const effectiveCRMOffer = effectiveCRMChannels(crmOfferedChannels, crmChannels);

    const toggleCRMChannel = async (channel) => {
        if (updatingCRMChannels || channel === 'browser') return;
        const selected = new Set(effectiveCRMOffer);
        if (selected.has(channel)) selected.delete(channel);
        else selected.add(channel);
        // Browser is the baseline CRM route whenever it is part of the pinned
        // authored profile. Messaging choices are additive and removable.
        if (crmChannels.some((item) => item.token === 'browser')) selected.add('browser');
        if (selected.size === 0) return;
        setUpdatingCRMChannels(true);
        try {
            await setCRMChannels?.(crmChannels
                .map((item) => item.token)
                .filter((token) => selected.has(token)));
        } finally {
            setUpdatingCRMChannels(false);
        }
    };

    // One control combines mode and the DCM execution-channel choice.
    const channelControl = (
        <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 'var(--font-size-xs)',
            fontFamily: 'var(--font-family-primary)',
            color: 'var(--color-text-secondary)',
            whiteSpace: 'nowrap',
        }}>
            Send method
            <select
                value={selectedMethod}
                disabled={sendInProgress}
                onChange={e => setStagingChannel?.(e.target.value)}
                style={{
                    padding: '2px 6px',
                    borderRadius: 'var(--radius-base)',
                    border: 'var(--border-thin) solid var(--color-primary-100)',
                    background: 'var(--color-bg-panel)',
                    color: 'var(--color-text-primary)',
                    fontSize: 'var(--font-size-xs)',
                    fontFamily: 'var(--font-family-primary)',
                    cursor: sendInProgress ? 'not-allowed' : 'pointer',
                }}
            >
                <option value="channel_response">Channel Request Method</option>
                <optgroup label="Send directly (DCM)">
                {selectableChannels().filter(c => directChannels.includes(c.token)).map(c => {
                    const control = controlFor(channelControls, c.token);
                    return (
                        <option key={c.token} value={c.token} disabled={control.enabled !== true}>
                            {c.name}
                        </option>
                    );
                })}
                </optgroup>
            </select>
        </label>
    );

    // Staged wears the same frame as every other Console panel (Panel.js, over
    // the shared component). It used to draw its own box, which came out a
    // lighter blue with a thicker border and a taller head than its neighbours.
    return (
        <Panel
            title="Staged"
            icon="staged"
            controls={channelControl}
            actions={actions}
            selection={count > 0}
            actionState={{ linkedSurveys }}
            style={{ minHeight: 120, ...style }}
            bodyStyle={{
                padding: 0, position: 'relative', overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
            }}
        >
            {dispatchMode === 'channel_response' && crmChannels.length > 0 && (
                <div style={{
                    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 12px',
                    padding: '8px 14px',
                    background: 'var(--color-primary-50)',
                    borderBottom: 'var(--border-thin) solid var(--color-primary-100)',
                    fontSize: 'var(--font-size-xs)',
                    fontFamily: 'var(--font-family-primary)',
                    color: 'var(--color-text-secondary)',
                    flexShrink: 0,
                }}>
                    <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-primary)' }}>
                        Offer on selection page
                    </span>
                    {crmChannels.map((channel) => {
                        const checked = effectiveCRMOffer.includes(channel.token);
                        const fixedBaseline = channel.token === 'browser';
                        return (
                            <label key={channel.token} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                cursor: fixedBaseline || sendInProgress || updatingCRMChannels ? 'default' : 'pointer',
                            }}>
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={fixedBaseline || sendInProgress || updatingCRMChannels}
                                    onChange={() => toggleCRMChannel(channel.token)}
                                    aria-label={`Offer ${channel.name} on the Channel Selection Page`}
                                />
                                <span>{channel.name}</span>
                                {fixedBaseline && <span style={{ color: 'var(--color-text-tertiary)' }}>(baseline)</span>}
                            </label>
                        );
                    })}
                </div>
            )}
            {reconcileNotice && (
                <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-sm)',
                    padding: 'var(--spacing-sm) var(--spacing-base)',
                    background: 'var(--color-primary-50)',
                    borderBottom: 'var(--border-thin) solid var(--color-primary-100)',
                    fontSize: 'var(--font-size-xs)',
                    fontFamily: 'var(--font-family-primary)',
                    color: 'var(--color-text-primary)',
                    lineHeight: 'var(--line-height-xs)',
                    flexShrink: 0,
                }}>
                    <span style={{ flex: 1 }}>{reconcileNotice}</span>
                    <button
                        type="button"
                        onClick={() => dismissReconcileNotice?.()}
                        aria-label="Dismiss notice"
                        style={{
                            border: 'none', background: 'transparent', cursor: 'pointer',
                            color: 'var(--color-text-secondary)', padding: 0,
                            fontSize: 'var(--font-size-md)', lineHeight: 1,
                        }}
                    >
                        &times;
                    </button>
                </div>
            )}

            {sendInProgress && !monitorOpen && sendResult && (
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '6px 14px',
                    background: 'var(--color-primary-50)',
                    borderTop: '1px solid var(--color-primary-100)',
                    borderBottom: '1px solid var(--color-primary-100)',
                    fontSize: 'var(--font-size-xs)',
                    fontFamily: 'var(--font-family-primary)',
                    color: 'var(--color-text-primary)',
                    flexShrink: 0,
                }}>
                    <span>
                        Send in progress: <strong>{sendResult.doneOverall} / {sendResult.totalOverall}</strong>
                        {sendResult.currentRecipient && (
                            <span style={{ color: 'var(--color-text-secondary)', marginLeft: 6 }}>
                                · {sendResult.currentRecipient.name || sendResult.currentRecipient.email || sendResult.currentRecipient.recipient_id}
                            </span>
                        )}
                    </span>
                    <button
                        type="button"
                        onClick={() => openSendMonitor?.()}
                        style={{
                            padding: '2px 10px',
                            border: '1px solid var(--color-primary-300)',
                            borderRadius: 'var(--radius-base)',
                            background: 'var(--color-bg-panel)',
                            color: 'var(--color-text-link)',
                            cursor: 'pointer',
                            fontSize: 'var(--font-size-xs)',
                            fontFamily: 'var(--font-family-primary)',
                            fontWeight: 'var(--font-weight-medium)',
                        }}
                    >
                        Show monitor
                    </button>
                </div>
            )}

            <div
                {...dropHandlers(
                    'staging-area',
                    (p) => p.type === 'survey' && p.status === 'deployed',
                    handleSurveyDrop
                )}
                style={{
                    flex: 1, overflow: 'auto',
                    padding: 14,
                    border: dropActive ? '2px dashed #1967D2' : '2px dashed transparent',
                    background: dropActive ? '#F1F7FF' : undefined,
                    transition: 'background 120ms ease, border-color 120ms ease',
                }}
            >
                {count === 0 ? (
                    <div style={{ color: '#9AA0A6', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>
                        Open Recipients or click + Recipients to start staging.
                    </div>
                ) : (
                    <>
                        <StagingSummaryBlock
                            count={count}
                            onEditSelection={() => onOpenRecipients?.([...stagedIds])}
                        />
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {[...stagedIds].map(id => (
                                <StagingRecipientChip
                                    key={id}
                                    recipientId={id}
                                    displayName={getRecipientDisplayName(id)}
                                    sentStatus={staging.getSentStatus?.(id) || 'neutral'}
                                    onRemove={remove}
                                    onContextMenu={(e) => handleChipContextMenu(e, id)}
                                />
                            ))}
                        </div>
                    </>
                )}
                <LinkedSurveysList linkedSurveys={linkedSurveys} onUnlink={unlinkSurvey} />
            </div>

            {confirmClear && (
                <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    padding: '10px 14px', background: '#FFF3E0', borderTop: '1px solid #FFB74D',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <span style={{ fontSize: 13, color: '#E65100' }}>
                        Clear all {count} recipient{count !== 1 ? 's' : ''}?
                    </span>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => setConfirmClear(false)} style={{
                            padding: '4px 12px', border: '1px solid #DADCE0', borderRadius: 4,
                            background: '#fff', cursor: 'pointer', fontSize: 12,
                        }}>Cancel</button>
                        <button onClick={() => { clear(); setConfirmClear(false); }} style={{
                            padding: '4px 12px', border: 'none', borderRadius: 4,
                            background: '#E65100', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        }}>Clear</button>
                    </div>
                </div>
            )}

            {contextMenuProps && <ContextMenu {...contextMenuProps} />}

            {showReLinkModal && (
                <ReLinkConfirmModal
                    deploymentName={showReLinkModal.surveyName}
                    newlyLinked={0}
                    alreadyLinked={count}
                    onConfirm={() => {
                        linkSurvey?.(showReLinkModal);
                        setShowReLinkModal(null);
                    }}
                    onCancel={() => setShowReLinkModal(null)}
                />
            )}

            {showSendModal && (
                <SendConfirmModal
                    recipientCount={count}
                    linkedSurveys={linkedSurveys}
                    recipients={recipients}
                    stagingChannel={stagingChannel}
                    dispatchMode={dispatchMode}
                    preview={dispatchPreview}
                    onConfirm={handleConfirmSend}
                    onCancel={() => setShowSendModal(false)}
                />
            )}

            {monitorOpen && sendResult && (
                <SendResultsModal
                    result={sendResult}
                    inProgress={sendInProgress}
                    onClose={() => {
                        // "Hide monitor" while in-flight, "Done" once complete.
                        // If the send is still running we just hide; if it's
                        // finished we clear the result so future sends start
                        // from a clean slate.
                        if (sendInProgress) closeSendMonitor?.();
                        else dismissSendResult?.();
                    }}
                    onCancelSend={cancelSend}
                />
            )}
        </Panel>
    );
}
